import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { useLanguage } from './LanguageContext';

// Define available color presets
export const colorPresets = {
    blue: { main: '#2563eb', light: '#3b82f6', dark: '#1d4ed8' },
    green: { main: '#16a34a', light: '#4ade80', dark: '#15803d' },
    purple: { main: '#9333ea', light: '#a855f7', dark: '#7e22ce' },
    amber: { main: '#d97706', light: '#f59e0b', dark: '#b45309' },
    red: { main: '#dc2626', light: '#f87171', dark: '#b91c1c' },
    teal: { main: '#0d9488', light: '#2dd4bf', dark: '#0f766e' },
    indigo: { main: '#4f46e5', light: '#6366f1', dark: '#4338ca' },
    pink: { main: '#db2777', light: '#ec4899', dark: '#be185d' },
};

export type ColorPresetName = keyof typeof colorPresets;
export type ViewMode = 'grid' | 'table';

// Define context shape
interface ThemeContextType {
    mode: 'light' | 'dark';
    toggleColorMode: () => void;
    primaryColor: ColorPresetName;
    setPrimaryColor: (color: ColorPresetName) => void;
    defaultViewMode: ViewMode;
    setDefaultViewMode: (mode: ViewMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeContext must be used within a ThemeProvider');
    }
    return context;
};

/**
 * Hook for CRUD pages using GenericPage/GenericTable to initialize viewMode ('grid' | 'table')
 * from the global default preference in localStorage, while allowing the user to toggle per-page.
 */
export const useDefaultViewMode = (initialOverride?: ViewMode): [ViewMode, (mode: ViewMode) => void] => {
    const { defaultViewMode } = useThemeContext();
    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        if (initialOverride) return initialOverride;
        const saved = localStorage.getItem('blobtic_default_view_mode');
        return (saved === 'grid' || saved === 'table') ? saved : defaultViewMode;
    });

    useEffect(() => {
        const handler = (e: Event) => {
            const customEvent = e as CustomEvent<ViewMode>;
            if (customEvent.detail === 'grid' || customEvent.detail === 'table') {
                setViewMode(customEvent.detail);
            }
        };
        window.addEventListener('blobtic_default_view_mode_changed', handler);
        return () => window.removeEventListener('blobtic_default_view_mode_changed', handler);
    }, []);

    return [viewMode, setViewMode];
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const { dir } = useLanguage();

    // Initialize mode from localStorage or system preference
    const [mode, setMode] = useState<'light' | 'dark'>(() => {
        const savedMode = localStorage.getItem('themeMode');
        return (savedMode === 'light' || savedMode === 'dark') ? savedMode : (prefersDarkMode ? 'dark' : 'light');
    });

    // Initialize color from localStorage or default to blue
    const [primaryColor, setPrimaryColor] = useState<ColorPresetName>(() => {
        const savedColor = localStorage.getItem('themeColor');
        return (savedColor && Object.keys(colorPresets).includes(savedColor))
            ? (savedColor as ColorPresetName)
            : 'blue';
    });

    // Initialize defaultViewMode from localStorage or default to 'grid'
    const [defaultViewMode, setDefaultViewModeState] = useState<ViewMode>(() => {
        const savedView = localStorage.getItem('blobtic_default_view_mode');
        return (savedView === 'grid' || savedView === 'table') ? savedView : 'grid';
    });

    useEffect(() => {
        localStorage.setItem('themeMode', mode);
    }, [mode]);

    useEffect(() => {
        localStorage.setItem('themeColor', primaryColor);
    }, [primaryColor]);

    useEffect(() => {
        localStorage.setItem('blobtic_default_view_mode', defaultViewMode);
    }, [defaultViewMode]);

    const setDefaultViewMode = (newMode: ViewMode) => {
        setDefaultViewModeState(newMode);
        localStorage.setItem('blobtic_default_view_mode', newMode);
        window.dispatchEvent(new CustomEvent<ViewMode>('blobtic_default_view_mode_changed', { detail: newMode }));
    };

    const colorMode = useMemo(() => ({
        mode,
        toggleColorMode: () => {
            setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
        },
        primaryColor,
        setPrimaryColor,
        defaultViewMode,
        setDefaultViewMode
    }), [mode, primaryColor, defaultViewMode]);

    // Emotion cache: RTL flips physical CSS properties (left/right, margins) when Arabic is selected
    const cache = useMemo(() =>
        dir === 'rtl'
            ? createCache({ key: 'muirtl', stylisPlugins: [rtlPlugin] })
            : createCache({ key: 'mui' })
    , [dir]);

    // Create MUI theme based on mode and color
    const theme = useMemo(() => createTheme({
        direction: dir,
        palette: {
            mode,
            primary: colorPresets[primaryColor],
            secondary: {
                main: '#1f2937',
                light: '#374151',
                dark: '#111827',
            },
            ...(mode === 'dark' ? {
                background: {
                    default: '#0f172a', // Slate 900
                    paper: '#1e293b',   // Slate 800
                },
                text: {
                    primary: '#f1f5f9',
                    secondary: '#94a3b8',
                }
            } : {
                background: {
                    default: '#f8fafc',
                    paper: '#ffffff',
                },
                text: {
                    primary: '#1e293b',
                    secondary: '#64748b',
                }
            }),
        },
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            h4: {
                fontWeight: 600,
                color: mode === 'dark' ? colorPresets[primaryColor].light : colorPresets[primaryColor].main,
            },
            h5: { fontWeight: 500 },
            h6: { fontWeight: 500 },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active': {
                        transition: 'background-color 5000s ease-in-out 0s',
                        WebkitTextFillColor: mode === 'dark' ? '#f1f5f9 !important' : '#1e293b !important',
                        caretColor: mode === 'dark' ? '#f1f5f9' : '#1e293b',
                        WebkitBoxShadow: mode === 'dark' ? '0 0 0 1000px #1e293b inset !important' : '0 0 0 1000px #ffffff inset !important',
                        borderRadius: 'inherit',
                    },
                    '.MuiFormLabel-asterisk, .MuiInputLabel-asterisk': {
                        color: '#ef4444 !important',
                        fontWeight: 'bold',
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    asterisk: {
                        color: '#ef4444 !important',
                        fontWeight: 'bold',
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    asterisk: {
                        color: '#ef4444 !important',
                        fontWeight: 'bold',
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        borderRadius: 8,
                        '&.Mui-disabled': {
                            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                            boxShadow: 'none',
                        },
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        boxShadow: mode === 'light'
                            ? '0 2px 8px rgba(0,0,0,0.1)'
                            : 'none',
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: colorPresets[primaryColor].main,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: colorPresets[primaryColor].main,
                        },
                    },
                },
            }
        }
    }), [mode, primaryColor, dir]);

    return (
        <ThemeContext.Provider value={colorMode}>
            <CacheProvider value={cache}>
                <MuiThemeProvider theme={theme}>
                    {children}
                </MuiThemeProvider>
            </CacheProvider>
        </ThemeContext.Provider>
    );
};
